import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Play, Users, CheckSquare, Square, Search, List, MessageSquare, Clock, Smartphone } from 'lucide-react';
import { Button, Card, useToast, Input } from '@caruso/ui';
import { flowsService } from '@/services/flows.service';
import { clientsService } from '@/services/clients.service';
import { listsService, ListMemberWithMessage } from '@/services/lists.service';
import { whatsappConnectionsService, WhatsAppConnection } from '@/services/whatsapp-connections.service';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Formata número de telefone brasileiro
function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return '';
  // Remove tudo que não é número
  const cleaned = phone.replace(/\D/g, '');
  // Formato brasileiro: 55 11 98181-7555
  if (cleaned.length === 13) {
    // Com código do país (55) + DDD (2) + número (9)
    return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 4)}) ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
  } else if (cleaned.length === 12) {
    // Com código do país (55) + DDD (2) + número (8)
    return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 4)}) ${cleaned.slice(4, 8)}-${cleaned.slice(8)}`;
  } else if (cleaned.length === 11) {
    // DDD (2) + número (9)
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  } else if (cleaned.length === 10) {
    // DDD (2) + número (8)
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
}

export function ExecuteFlowPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [overrideListId, setOverrideListId] = useState<string>('');
  const [selectedConnectionId, setSelectedConnectionId] = useState<string>('');

  const { data: flow } = useQuery({
    queryKey: ['flow', id],
    queryFn: () => flowsService.getById(id!),
    enabled: !!id,
  });

  // Query para buscar todas as listas disponíveis
  const { data: allLists } = useQuery({
    queryKey: ['lists-for-execute'],
    queryFn: () => listsService.getAll({ page: 1, limit: 100, activeOnly: true }),
  });

  // Query para buscar conexões WhatsApp disponíveis
  const { data: whatsappConnections } = useQuery({
    queryKey: ['whatsapp-connections'],
    queryFn: () => whatsappConnectionsService.getAll(),
  });

  // Filtrar apenas conexões ativas (status OPEN)
  const activeConnections = useMemo(() => {
    return whatsappConnections?.filter((c: WhatsAppConnection) => c.status === 'OPEN') || [];
  }, [whatsappConnections]);

  // Selecionar conexão padrão automaticamente quando carregar
  useEffect(() => {
    if (activeConnections.length > 0 && !selectedConnectionId) {
      const defaultConnection = activeConnections.find((c: WhatsAppConnection) => c.isDefault);
      if (defaultConnection) {
        setSelectedConnectionId(defaultConnection.id);
      } else {
        // Se não tem padrão, seleciona a primeira
        setSelectedConnectionId(activeConnections[0].id);
      }
    }
  }, [activeConnections.length]); // Só roda quando activeConnections muda

  // Lista atualmente selecionada (override ou padrão do fluxo)
  const currentListId = overrideListId || flow?.listId || '';

  // Query para buscar membros com última mensagem
  const { data: members, isLoading: isLoadingMembers } = useQuery({
    queryKey: ['list-members-with-messages', currentListId],
    queryFn: () => listsService.getMembersWithMessages(currentListId),
    enabled: !!currentListId,
  });

  // Query para buscar todos os clientes (quando não há lista)
  const { data: allClientsResponse, isLoading: isLoadingAllClients } = useQuery({
    queryKey: ['all-clients-for-flow'],
    queryFn: () => clientsService.getAll({ page: 1, pageSize: 1000, archived: false }),
    enabled: !currentListId,
  });

  // Mapear clientes da lista ou todos os clientes
  const clients = useMemo(() => {
    if (currentListId && members) {
      return members.map((m: ListMemberWithMessage) => ({
        id: m.client.id,
        name: m.client.name,
        email: m.client.email,
        phone: m.client.phone,
        document: m.client.document,
        lastMessage: m.client.lastMessage,
      }));
    } else if (!currentListId && allClientsResponse) {
      return allClientsResponse.data.map((c: any) => ({
        id: c.id,
        name: c.name,
        email: c.email,
        phone: c.phone,
        document: c.document,
        lastMessage: null,
      }));
    }
    return [];
  }, [currentListId, members, allClientsResponse]);

  // Quando muda a lista, seleciona todos os clientes por padrão
  useEffect(() => {
    if (clients.length > 0) {
      setSelectedClients(clients.map((c: any) => c.id));
    }
  }, [currentListId, clients.length]);

  // Inicializa overrideListId com o listId do fluxo quando carrega
  useEffect(() => {
    if (flow?.listId && !overrideListId) {
      setOverrideListId(flow.listId);
    }
  }, [flow?.listId]);

  // Filter clients based on search query
  const filteredClients = useMemo(() => {
    if (!clients) return [];
    if (!searchQuery.trim()) return clients;

    const query = searchQuery.toLowerCase();
    return clients.filter((client: any) => {
      return (
        client.name?.toLowerCase().includes(query) ||
        client.email?.toLowerCase().includes(query) ||
        client.phone?.toLowerCase().includes(query) ||
        client.document?.toLowerCase().includes(query)
      );
    });
  }, [clients, searchQuery]);

  const startFlowMutation = useMutation({
    mutationFn: () => {
      // Usa os clientes selecionados e conexão WhatsApp
      return flowsService.startFlow(id!, {
        clientIds: selectedClients,
        whatsappConnectionId: selectedConnectionId || undefined,
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['flows'] });
      queryClient.invalidateQueries({ queryKey: ['flow-executions'] });
      toast({
        title: 'Fluxo iniciado!',
        description: `${data.executions.length} execuções criadas${data.skipped > 0 ? ` (${data.skipped} ignorados)` : ''}`,
        variant: 'success',
      });
      navigate('/app/fluxos');
    },
    onError: () => {
      toast({
        title: 'Erro ao iniciar fluxo',
        variant: 'destructive',
      });
    },
  });

  const toggleClient = (clientId: string) => {
    setSelectedClients((prev) =>
      prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId]
    );
  };

  const toggleAll = () => {
    if (selectedClients.length === filteredClients?.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients?.map((c: any) => c.id) || []);
    }
  };

  const handleStart = () => {
    if (selectedClients.length === 0) {
      toast({
        title: 'Selecione clientes',
        description: 'Selecione pelo menos um cliente para executar o fluxo',
        variant: 'destructive',
      });
      return;
    }

    if (activeConnections.length > 0 && !selectedConnectionId) {
      toast({
        title: 'Selecione uma conexão WhatsApp',
        description: 'Escolha uma conexão WhatsApp ativa para enviar as mensagens',
        variant: 'destructive',
      });
      return;
    }

    const connectionName = activeConnections.find((c: WhatsAppConnection) => c.id === selectedConnectionId)?.name || 'padrão';
    if (confirm(`Iniciar fluxo para ${selectedClients.length} cliente(s) usando a conexão "${connectionName}"?`)) {
      startFlowMutation.mutate();
    }
  };

  const formatLastMessageDate = (date: string) => {
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true, locale: ptBR });
    } catch {
      return '-';
    }
  };

  const isLoading = isLoadingMembers || isLoadingAllClients;

  if (!flow) {
    return <div className="text-white">Carregando...</div>;
  }

  // Lista selecionada atualmente (para mostrar o nome)
  const selectedList = allLists?.data.find((l) => l.id === currentListId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/app/fluxos')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-4xl font-bold text-white">Executar Fluxo</h1>
          <p className="text-brand-ivory/80 mt-1 text-lg">{flow.name}</p>
        </div>
        <Button
          onClick={handleStart}
          disabled={selectedClients.length === 0 || startFlowMutation.isPending || (activeConnections.length > 0 && !selectedConnectionId)}
          size="lg"
        >
          <Play className="w-5 h-5 mr-2" />
          {startFlowMutation.isPending
            ? 'Iniciando...'
            : `Iniciar para ${selectedClients.length} cliente(s)`}
        </Button>
      </div>

      {/* Flow Details */}
      <Card className="p-6 bg-brand-smoke border-brand-gold/20">
        <h2 className="text-xl font-bold text-white mb-4">Detalhes do Fluxo</h2>
        {flow.description && <p className="text-brand-ivory/70 mb-4">{flow.description}</p>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-brand-ivory/60">Lista Padrão</p>
            <p className="text-white font-medium">{flow.list?.name || 'Nenhuma'}</p>
          </div>
          <div>
            <p className="text-brand-ivory/60">Intervalo Mensagens</p>
            <p className="text-white font-medium">{flow.delayBetweenMessages}s</p>
          </div>
          <div>
            <p className="text-brand-ivory/60">Intervalo Clientes</p>
            <p className="text-white font-medium">{flow.delayBetweenClients}s</p>
          </div>
          <div>
            <p className="text-brand-ivory/60">Horário</p>
            <p className="text-white font-medium">
              {flow.startTime} - {flow.endTime}
            </p>
          </div>
        </div>
      </Card>

      {/* List Selection */}
      <Card className="p-6 bg-brand-smoke border-brand-gold/20">
        <div className="flex items-center gap-3 mb-4">
          <List className="w-5 h-5 text-brand-gold" />
          <h2 className="text-xl font-bold text-white">Lista para Disparo</h2>
        </div>

        <div className="mb-4">
          <select
            value={overrideListId}
            onChange={(e) => {
              setOverrideListId(e.target.value);
              setSelectedClients([]);
              setSearchQuery('');
            }}
            className="w-full p-3 bg-brand-ink/50 border border-brand-gold/20 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
          >
            <option value="" className="bg-neutral-900">Todos os clientes (sem lista)</option>
            {allLists?.data.map((list) => (
              <option key={list.id} value={list.id} className="bg-neutral-900">
                {list.name} ({list.membersCount} membros)
              </option>
            ))}
          </select>
          {selectedList && (
            <p className="text-brand-ivory/60 text-sm mt-2">
              Lista selecionada: <span className="text-brand-gold">{selectedList.name}</span> com {selectedList.membersCount} membros
            </p>
          )}
        </div>
      </Card>

      {/* WhatsApp Connection Selection */}
      <Card className="p-6 bg-brand-smoke border-brand-gold/20">
        <div className="flex items-center gap-3 mb-4">
          <Smartphone className="w-5 h-5 text-green-400" />
          <h2 className="text-xl font-bold text-white">Conexão WhatsApp</h2>
        </div>

        {activeConnections.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-brand-ivory/60">Nenhuma conexão WhatsApp ativa.</p>
            <p className="text-brand-ivory/40 text-sm mt-1">
              Configure e conecte uma conta em Configurações &gt; WhatsApp.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <select
              value={selectedConnectionId}
              onChange={(e) => setSelectedConnectionId(e.target.value)}
              className="w-full p-3 bg-brand-ink/50 border border-brand-gold/20 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
            >
              <option value="" className="bg-neutral-900">Selecione uma conexão</option>
              {activeConnections.map((connection: WhatsAppConnection) => (
                <option key={connection.id} value={connection.id} className="bg-neutral-900">
                  {connection.name} {connection.phoneNumber ? `(${formatPhoneNumber(connection.phoneNumber)})` : ''} {connection.isDefault ? '⭐ Padrão' : ''}
                </option>
              ))}
            </select>
            {selectedConnectionId && (
              <p className="text-green-400 text-sm flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                Conexão ativa e pronta para envio
              </p>
            )}
          </div>
        )}
      </Card>

      {/* Client Selection */}
      <Card className="p-6 bg-brand-smoke border-brand-gold/20">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">
            Selecionar Clientes ({selectedClients.length} de {clients.length} selecionados)
          </h2>
          <Button onClick={toggleAll} variant="outline" size="sm">
            {selectedClients.length === filteredClients?.length ? (
              <>
                <Square className="w-4 h-4 mr-2" />
                Desmarcar Todos
              </>
            ) : (
              <>
                <CheckSquare className="w-4 h-4 mr-2" />
                Marcar Todos
              </>
            )}
          </Button>
        </div>

        {/* Search Field */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-brand-ivory/40" />
            <Input
              type="text"
              placeholder="Buscar por nome, email, telefone ou documento..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-brand-ink/30 border-brand-gold/20 text-white placeholder:text-brand-ivory/40"
            />
          </div>
          {searchQuery && (
            <p className="text-sm text-brand-ivory/60 mt-2">
              {filteredClients.length} cliente(s) encontrado(s)
            </p>
          )}
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-brand-gold/20 border-t-brand-gold rounded-full animate-spin mx-auto mb-4" />
            <p className="text-brand-ivory/60">Carregando clientes...</p>
          </div>
        ) : filteredClients && filteredClients.length > 0 ? (
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {filteredClients.map((client: any) => (
              <label
                key={client.id}
                className={`flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-colors border ${
                  selectedClients.includes(client.id)
                    ? 'bg-brand-gold/10 border-brand-gold/30'
                    : 'bg-brand-ink/30 border-brand-gold/10 hover:bg-brand-ink/50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedClients.includes(client.id)}
                  onChange={() => toggleClient(client.id)}
                  className="w-5 h-5 mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-medium">{client.name || 'Sem nome'}</p>
                    {client.phone && (
                      <span className="text-brand-ivory/50 text-sm">• {client.phone}</span>
                    )}
                  </div>
                  {client.email && (
                    <p className="text-brand-ivory/50 text-sm truncate">{client.email}</p>
                  )}

                  {/* Last Message Info */}
                  {client.lastMessage ? (
                    <div className="mt-2 p-2 bg-brand-ink/50 rounded border border-brand-gold/10">
                      <div className="flex items-center gap-2 text-xs text-brand-ivory/60 mb-1">
                        <MessageSquare className="w-3 h-3" />
                        <span>Última mensagem</span>
                        <Clock className="w-3 h-3 ml-2" />
                        <span>{formatLastMessageDate(client.lastMessage.sentAt)}</span>
                        {client.lastMessage.flowExecution?.flow?.name && (
                          <span className="text-brand-gold ml-2">
                            via {client.lastMessage.flowExecution.flow.name}
                          </span>
                        )}
                      </div>
                      <p className="text-brand-ivory/80 text-sm line-clamp-2">
                        {client.lastMessage.content}
                      </p>
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-brand-ivory/40 flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" />
                      Nenhuma mensagem enviada anteriormente
                    </div>
                  )}
                </div>
              </label>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-brand-gold/50 mx-auto mb-4" />
            <p className="text-brand-ivory/60">
              {searchQuery ? 'Nenhum cliente encontrado com esse filtro' : 'Nenhum cliente disponível'}
            </p>
            {!currentListId && (
              <p className="text-brand-ivory/40 text-sm mt-2">
                Selecione uma lista acima ou cadastre novos clientes
              </p>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
